import { List, Spin, Empty, theme, Drawer, Space, Switch } from "antd";
import { SmallTitle } from "src/components/ListPage";
import { CardRenderer } from "../cards/CardRenderer";
import { AnnotatedDeepLink, DigestV2State } from "pages/api/deep-links/types";
import { CardRegistryProvider } from "../cards/CardRegistryContext";
import { motion } from "framer-motion";
import { FilterControls } from "./FilterControls";
import { CategorizedTags } from "./CategorizedTags";
import React, { CSSProperties } from "react";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useFeed } from "../hooks/useFeed";
import { useApplicationContext } from "src/hooks/useApplication";
import { usePromptInstructions } from "src/hooks/usePromptInstructions";
import { PromptInstructionsContext } from "./PromptInstructionsContext";

import { EmptyFeedCard } from "./EmptyFeedCard";
import { CssVars } from "src/components/CssVars";
import { TopicHeader } from "../cards/components/TopicHeader";
import { useDrawerWidth } from "../cards/cardUtils";
import { SidebarIcon } from "src/components/SidebarIcon";

import styles from "./topicfeed.module.css";

// --- Pure Presentational TopicFeed ---

export function TopicFeed({
  items,
  size = "mini",
  className,
  showErrorCards,
  onCardClick,
  isLoading,
  hasMore,
  loadingRef,
}: {
  items: AnnotatedDeepLink<DigestV2State>[];
  size?: "mini" | "expanded";
  className?: string;
  showErrorCards?: boolean;
  onCardClick?: (item: AnnotatedDeepLink<DigestV2State>) => void;
  isLoading?: boolean;
  hasMore?: boolean;
  loadingRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <>
      <List
        className={`${styles.topic_feed} ${className || ""}`}
        dataSource={items}
        renderItem={(item, index) => (
          <motion.div
            key={`${index}-${item.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
              layout: { duration: 0.3 },
            }}
            layout
            onClick={() => onCardClick?.(item)}
          >
            {item.json.topicCardConfig && (
              <CardRenderer
                link={item as AnnotatedDeepLink<DigestV2State>}
                size={size}
                cardType="feed"
                showErrorCards={showErrorCards}
              />
            )}
          </motion.div>
        )}
        locale={{ emptyText: <Empty description="No insights available" /> }}
      />
      {isLoading && <Spin style={{ display: "block", margin: "16px auto" }} />}
      {hasMore && (
        <div ref={loadingRef} style={{ height: 50, textAlign: "center" }}>
          <Spin />
        </div>
      )}
    </>
  );
}

// --- Smart Container ---

export function TopicFeedWrapper({ className, style, config }: any) {
  const { token } = theme.useToken();
  const [selectedCard, setSelectedCard] =
    React.useState<AnnotatedDeepLink<DigestV2State> | null>(null);
  const app = useApplicationContext();
  const canEdit =
    selectedCard?.userId === app.user?.id ||
    (selectedCard?.orgId && app.isOrgAdmin) ||
    app.isSuperAdmin;
  const [showErrorCards, setShowErrorCards] = React.useState(false);
  const {
    items,
    isLoading,
    hasMore,
    loadMore,
    libraryFilter,
    setLibraryFilter,
    availableTags,
  } = useFeed();
  const { loadingRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore: loadMore,
  });
  const [drawerTopicState, setDrawerTopicState] = React.useState<any | null>(
    null
  );
  const [isDrawerLoading, setIsDrawerLoading] = React.useState(false);
  const promptInstructions = usePromptInstructions();
  const mainContentStyles = React.useMemo<CSSProperties>(
    () => ({ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }),
    []
  );
  const drawerWidth = useDrawerWidth();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 1100px)");
    let lastMatches = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      if (!lastMatches && e.matches) setSidebarOpen(false);
      lastMatches = e.matches;
    };
    mq.addEventListener("change", handler);
    if (mq.matches) setSidebarOpen(false);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const handleCardClick = React.useCallback(
    (item: AnnotatedDeepLink<DigestV2State>) => {
      if (config?.enableCardClick) {
        setSelectedCard(item);
        setDrawerTopicState({ ...item.json });
        setIsDrawerLoading(false);
      }
    },
    [config?.enableCardClick]
  );
  const handleCloseDrawer = React.useCallback(() => {
    setSelectedCard(null);
    setDrawerTopicState(null);
    setIsDrawerLoading(false);
  }, []);
  const handleDrawerUpdateFilters = (newFilters: any[]) => {
    setIsDrawerLoading(true);
    setDrawerTopicState((prev: any) => ({
      ...prev,
      defaultFilters: newFilters,
    }));
  };
  const handleDrawerUpdateTimeframe = (newDateRange: any) => {
    setIsDrawerLoading(true);
    setDrawerTopicState((prev: any) => ({
      ...prev,
      defaultDateRange: newDateRange,
    }));
  };

  // Only after all hooks, handle early returns
  if (promptInstructions.loading || isLoading) return <Spin />;
  if (promptInstructions.error)
    return <Empty description="Error loading prompt instructions" />;

  return (
    <PromptInstructionsContext.Provider value={promptInstructions.prefixes}>
      <CardRegistryProvider>
        <div
          className={className}
          style={{
            display: "flex",
            maxWidth: 1400,
            margin: "0 5% 0 5%",
            ...style,
          }}
        >
          {/* Main Content */}
          <div style={mainContentStyles}>
            <div>
              {items.length === 0 ? (
                <EmptyFeedCard />
              ) : (
                <TopicFeed
                  items={items}
                  size={config?.defaultSize || "mini"}
                  className={className}
                  showErrorCards={showErrorCards}
                  onCardClick={handleCardClick}
                  isLoading={isLoading}
                  hasMore={hasMore}
                  loadingRef={loadingRef}
                />
              )}
            </div>
          </div>
          {/* right Sidebar */}
          <div
            className={
              styles.topic_feed_sidebar +
              " " +
              (sidebarOpen ? styles.sidebar_open : styles.sidebar_closed)
            }
          >
            {/* Toggle button always visible at the top */}
            <div className={styles.sidebar_toggle_btn_wrapper}>
              <button
                className={styles.sidebar_toggle_btn}
                onClick={() => setSidebarOpen((open) => !open)}
                aria-label={sidebarOpen ? "Hide filters" : "Show filters"}
              >
                <SidebarIcon style={{ fontSize: 22 }} />
              </button>
            </div>
            {/* Sidebar content only if open */}
            {sidebarOpen && (
              <div style={{ flex: 1, overflow: "auto" }}>
                <FilterControls
                  libraryFilter={libraryFilter}
                  setLibraryFilter={setLibraryFilter}
                />
                <CategorizedTags
                  availableTags={availableTags}
                  libraryFilter={libraryFilter}
                  setLibraryFilter={setLibraryFilter}
                  style={{ marginTop: 24 }}
                />
                {app.isSuperAdmin && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ padding: "12px 0px" }}>
                      <SmallTitle text="ADMIN SETTINGS" />
                    </div>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Show Error Cards</span>
                        <Switch
                          checked={showErrorCards}
                          onChange={setShowErrorCards}
                          size="small"
                        />
                      </div>
                    </Space>
                  </div>
                )}
              </div>
            )}
          </div>
          <Drawer
            open={selectedCard !== null}
            onClose={handleCloseDrawer}
            title={
              selectedCard && drawerTopicState ? (
                <TopicHeader
                  title={selectedCard.name}
                  link={selectedCard}
                  topic={drawerTopicState}
                  canEdit={canEdit}
                  onUpdateFilters={handleDrawerUpdateFilters}
                  isLoading={isDrawerLoading}
                  onUpdateTimeframe={handleDrawerUpdateTimeframe}
                />
              ) : null
            }
            width={drawerWidth}
            placement="right"
            style={{
              padding: "8px 24px 24px 24px",
              backgroundColor: token.colorBgBase,
            }}
            headerStyle={{ padding: "4px 24px" }}
          >
            <CssVars>
              {selectedCard?.json && drawerTopicState && (
                <CardRenderer
                  link={
                    {
                      ...selectedCard,
                      json: drawerTopicState,
                    } as AnnotatedDeepLink<DigestV2State>
                  }
                  size="expanded"
                  showErrorCards={showErrorCards}
                />
              )}
            </CssVars>
          </Drawer>
        </div>
      </CardRegistryProvider>
    </PromptInstructionsContext.Provider>
  );
}
